/**
 * Owner-only Compliance & Service-Management page (Phase 2).
 *
 * Implements the four compliance items from the 19-item backlog:
 *   #9  External-audit evidence pack — date-ranged JSON export
 *   #10 GDPR DSAR — Article-15 export + Article-17 erasure
 *   #11 Sub-processor change log — register + add/edit/remove
 *   #12 Tenant offboarding — state-machine workflow with certificate
 */
import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";
import {
  FileSearch, FileLock, Network, LogOut, Loader2, Download, Plus, Trash2,
  CheckCircle2, AlertTriangle, ArrowRight,
} from "lucide-react";

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <section className="rounded-2xl border border-black/5 bg-white p-6" data-testid={testid}>
    <header className="mb-4 flex items-start gap-3">
      <div className="rounded-xl bg-ek-purple/10 p-2 text-ek-purple"><Icon className="h-5 w-5" /></div>
      <div className="flex-1">
        <h2 className="font-display text-lg font-semibold text-ek-ink">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-ek-ink/60">{subtitle}</p>}
      </div>
    </header>
    {children}
  </section>
);

// ---------------------------------------------------------------------------
// #9 Evidence pack
// ---------------------------------------------------------------------------

function EvidencePackSection() {
  const [days, setDays] = useState(90);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/owner/evidence-pack?days=${days}`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `erio-evidence-pack-${new Date().toISOString().split("T")[0]}-${days}d.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Evidence pack downloaded (${days} days).`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Evidence pack failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      icon={FileSearch}
      title="External-audit evidence pack"
      subtitle="One-click bundle of controls, attestations, sub-processors, incidents and a slice of the audit log — for SOC 2 / ISO 27001 reviewers."
      testid="evidence-pack-section"
    >
      <div className="flex flex-wrap items-center gap-3">
        <label className="text-xs text-ek-ink/70">
          Period (days)
          <input
            type="number" min="1" max="365" value={days}
            onChange={(e) => setDays(Math.max(1, Math.min(365, parseInt(e.target.value) || 90)))}
            className="ml-2 w-20 rounded-md border border-black/10 px-2 py-1 text-sm"
            data-testid="evidence-pack-days"
          />
        </label>
        <button
          onClick={download} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple-dark disabled:opacity-50"
          data-testid="evidence-pack-download"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Download JSON
        </button>
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// #10 GDPR DSAR
// ---------------------------------------------------------------------------

function DsarSection() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async (op) => {
    if (!email.trim()) { toast.error("Email required"); return; }
    if (op === "erase" && !confirm(`Permanently ERASE all platform data for ${email}? This cannot be undone.`)) return;
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/owner/dsar/${op}`, { email: email.trim() });
      setResult({ op, data: r.data });
      // Also offer a download of the JSON
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `dsar-${op}-${Date.now()}.json`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`DSAR ${op} complete.`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || `DSAR ${op} failed`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section
      icon={FileLock}
      title="GDPR DSAR — export & erasure"
      subtitle="UK GDPR Art. 15 (data export) and Art. 17 (right to erasure). Both produce a downloadable JSON receipt with SHA-256 of the email for audit, NOT the plaintext."
      testid="dsar-section"
    >
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="data-subject@example.com"
          className="min-w-[280px] flex-1 rounded-md border border-black/10 px-3 py-1.5 text-sm"
          data-testid="dsar-email"
        />
        <button
          onClick={() => run("export")} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple-dark disabled:opacity-50"
          data-testid="dsar-export-btn"
        >
          <Download className="h-3.5 w-3.5" /> Export (Art. 15)
        </button>
        <button
          onClick={() => run("erase")} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
          data-testid="dsar-erase-btn"
        >
          <Trash2 className="h-3.5 w-3.5" /> Erase (Art. 17)
        </button>
      </div>
      {result && (
        <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-900" data-testid="dsar-result">
          <CheckCircle2 className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
          {result.op === "export"
            ? `Export complete: ${result.data.total_records} records across ${Object.keys(result.data.collections).length} collections.`
            : `Erasure complete: ${result.data.total_records_erased} records removed. Receipt downloaded.`}
        </div>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// #11 Sub-processors
// ---------------------------------------------------------------------------

function SubProcessorsSection() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ slug: "", name: "", purpose: "", country: "" });

  const load = () => {
    api.get("/owner/sub-processors").then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/owner/sub-processors", {
        slug: form.slug.trim().toLowerCase(),
        name: form.name.trim(),
        purpose: form.purpose.trim(),
        country: form.country.trim() || null,
        data_categories: [],
      });
      toast.success(`Added ${form.name}. 30-day customer notification window opened.`);
      setForm({ slug: "", name: "", purpose: "", country: "" });
      setAdding(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Add failed");
    }
  };

  const remove = async (slug) => {
    if (!confirm(`Mark sub-processor ${slug} as removed?`)) return;
    try {
      await api.delete(`/owner/sub-processors/${slug}`);
      toast.success("Removal logged.");
      load();
    } catch (err) {
      toast.error("Remove failed");
    }
  };

  return (
    <Section
      icon={Network}
      title="Sub-processor register"
      subtitle="GDPR Art. 28 sub-processor list. Adding / removing triggers a 30-day customer notification window logged to the change-log."
      testid="sub-processors-section"
    >
      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && (
        <>
          <div className="mb-3 flex items-center justify-between">
            <p className="text-xs text-ek-ink/60">{data.count} sub-processor{data.count === 1 ? "" : "s"} on file</p>
            <button
              onClick={() => setAdding(!adding)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ek-purple/30 px-3 py-1 text-xs font-semibold text-ek-purple hover:bg-ek-purple/5"
              data-testid="sub-processor-add-toggle"
            >
              <Plus className="h-3.5 w-3.5" /> {adding ? "Cancel" : "Add new"}
            </button>
          </div>

          {adding && (
            <form onSubmit={add} className="mb-4 grid gap-2 rounded-lg border border-black/10 p-3 text-sm" data-testid="sub-processor-add-form">
              <input required placeholder="slug (e.g. resend)" value={form.slug} onChange={(e) => setForm({...form, slug: e.target.value})} className="rounded border border-black/10 px-2 py-1" data-testid="sub-processor-form-slug" />
              <input required placeholder="Legal name (e.g. Resend Inc.)" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="rounded border border-black/10 px-2 py-1" data-testid="sub-processor-form-name" />
              <input required placeholder="Purpose (e.g. Transactional email)" value={form.purpose} onChange={(e) => setForm({...form, purpose: e.target.value})} className="rounded border border-black/10 px-2 py-1" data-testid="sub-processor-form-purpose" />
              <input placeholder="Country (e.g. US, GB, IE)" value={form.country} onChange={(e) => setForm({...form, country: e.target.value})} maxLength="4" className="rounded border border-black/10 px-2 py-1" data-testid="sub-processor-form-country" />
              <button type="submit" className="self-start rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white" data-testid="sub-processor-form-submit">Add &amp; notify customers</button>
            </form>
          )}

          <table className="min-w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-ek-ink/50 text-left">
              <tr>
                <th className="py-1">Name</th>
                <th className="py-1">Purpose</th>
                <th className="py-1">Country</th>
                <th className="py-1">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((s) => (
                <tr key={s.slug} className="border-t border-black/5" data-testid={`sub-processor-${s.slug}`}>
                  <td className="py-1.5 font-medium">{s.name}<span className="ml-2 font-mono text-[10px] text-ek-ink/40">{s.slug}</span></td>
                  <td className="py-1.5 text-ek-ink/70">{s.purpose}</td>
                  <td className="py-1.5 text-xs">{s.country || "—"}</td>
                  <td className="py-1.5">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      s.status === "active" ? "bg-emerald-100 text-emerald-800"
                      : s.status === "deprecated" ? "bg-amber-100 text-amber-800"
                      : "bg-slate-100 text-slate-700"}`}>{s.status}</span>
                  </td>
                  <td className="py-1.5 text-right">
                    {s.status === "active" && (
                      <button onClick={() => remove(s.slug)} className="text-xs text-rose-600 hover:underline" data-testid={`sub-processor-remove-${s.slug}`}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------------------
// #12 Tenant offboarding
// ---------------------------------------------------------------------------

const STATE_BG = {
  requested: "bg-slate-100 text-slate-700",
  data_exported: "bg-violet-100 text-violet-800",
  cooling_off: "bg-amber-100 text-amber-800",
  hard_deleted: "bg-rose-100 text-rose-800",
  certified: "bg-emerald-100 text-emerald-800",
};

function OffboardingSection() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ tenant_id: "", requestor_email: "", reason: "" });
  const [creating, setCreating] = useState(false);

  const load = () => api.get("/owner/tenant-offboarding").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const request = async (e) => {
    e.preventDefault();
    try {
      await api.post("/owner/tenant-offboarding", form);
      toast.success("Offboarding requested.");
      setForm({ tenant_id: "", requestor_email: "", reason: "" });
      setCreating(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Request failed");
    }
  };

  const advance = async (offId) => {
    if (!confirm("Advance this offboarding to the next state? Some steps are destructive (hard delete cannot be undone).")) return;
    try {
      await api.post(`/owner/tenant-offboarding/${offId}/advance`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Advance failed");
    }
  };

  return (
    <Section
      icon={LogOut}
      title="Tenant offboarding workflow"
      subtitle="GDPR right-to-erasure for departing tenants. Five-state machine ends with a signed destruction certificate."
      testid="offboarding-section"
    >
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-ek-ink/60">
          {data ? `${data.count} offboarding${data.count === 1 ? "" : "s"} on file` : ""}
        </p>
        <button
          onClick={() => setCreating(!creating)}
          className="inline-flex items-center gap-1.5 rounded-lg border border-ek-purple/30 px-3 py-1 text-xs font-semibold text-ek-purple hover:bg-ek-purple/5"
          data-testid="offboarding-new-toggle"
        >
          <Plus className="h-3.5 w-3.5" /> {creating ? "Cancel" : "New offboarding"}
        </button>
      </div>

      {creating && (
        <form onSubmit={request} className="mb-4 grid gap-2 rounded-lg border border-black/10 p-3 text-sm" data-testid="offboarding-form">
          <input required placeholder="tenant_id (e.g. tnt_acme)" value={form.tenant_id} onChange={(e) => setForm({...form, tenant_id: e.target.value})} className="rounded border border-black/10 px-2 py-1" data-testid="offboarding-form-tenant" />
          <input required type="email" placeholder="Requestor email (their primary contact)" value={form.requestor_email} onChange={(e) => setForm({...form, requestor_email: e.target.value})} className="rounded border border-black/10 px-2 py-1" data-testid="offboarding-form-email" />
          <input placeholder="Reason (optional)" value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} className="rounded border border-black/10 px-2 py-1" data-testid="offboarding-form-reason" />
          <button type="submit" className="self-start rounded-lg bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white" data-testid="offboarding-form-submit">Request offboarding</button>
        </form>
      )}

      {!data && <Loader2 className="h-4 w-4 animate-spin text-ek-ink/40" />}
      {data && data.items.length === 0 && <p className="text-xs text-ek-ink/40">No offboardings on file.</p>}
      {data && data.items.length > 0 && (
        <ul className="space-y-2" data-testid="offboarding-list">
          {data.items.map((o) => (
            <li key={o.offboarding_id} className="rounded-lg border border-black/5 p-3" data-testid={`offboarding-${o.offboarding_id}`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs">{o.offboarding_id}</span>
                <span className="font-medium text-ek-ink">{o.tenant_name}</span>
                <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATE_BG[o.state]}`}>
                  {o.state.replace(/_/g, " ")}
                </span>
                {o.state !== "certified" && (
                  <button
                    onClick={() => advance(o.offboarding_id)}
                    className="ml-auto inline-flex items-center gap-1 rounded-md bg-ek-purple px-2 py-0.5 text-[11px] font-semibold text-white"
                    data-testid={`offboarding-advance-${o.offboarding_id}`}
                  >
                    Advance <ArrowRight className="h-3 w-3" />
                  </button>
                )}
              </div>
              <p className="mt-1 text-xs text-ek-ink/60">
                Requestor: {o.requestor_email}
                {o.cooling_off_ends && (
                  <> · Cooling-off ends {new Date(o.cooling_off_ends).toLocaleDateString()}</>
                )}
                {o.certificate && (
                  <> · <span className="font-mono">cert {o.certificate.signature_hash.slice(0, 12)}…</span></>
                )}
              </p>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

export default function ComplianceOps() {
  return (
    <>
      <Seo title="Compliance & Service Ops · ERIO" />
      <Navbar />
      <main className="mx-auto max-w-6xl space-y-6 px-4 py-8" data-testid="compliance-ops-page">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-ek-purple font-semibold">
            EKSELENS · ERIO · OWNER
          </p>
          <h1 className="mt-2 font-display text-3xl font-bold text-ek-ink">
            Compliance & Service Operations
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-ek-ink/70">
            Phase 2 of the governance backlog: audit evidence packs, GDPR
            DSAR fulfilment, sub-processor change-log, and tenant
            offboarding workflow.
          </p>
        </header>
        <EvidencePackSection />
        <DsarSection />
        <SubProcessorsSection />
        <OffboardingSection />
      </main>
    </>
  );
}
